/**
 * Permissions pour le module Scan de Documents (GED)
 *
 * Rôles autorisés à accéder à la GED :
 *
 * - admin        : accès complet à la GED (lecture + modification)
 * - production   : lecture des BL uniquement
 * - be           : lecture des BL uniquement
 * - gestion      : lecture des BL uniquement
 * - comptabilite : factures uniquement (lecture, montants, export, workflow facture)
 * - client       : aucun accès
 *
 * Tout autre rôle n'a aucun accès à la GED.
 *
 * NB : Les documents de type contrat/assurance ne sont pas concernés par ces restrictions workflow.
 *      Seuls les BL et factures suivent ce modèle de droits.
 */

// Codes des types de document servant aux restrictions par rôle
export const BL_TYPE_CODE = 'bon-de-livraison';
export const FACTURE_TYPE_CODE = 'facture';

// Rôles n'ayant accès qu'en lecture, et uniquement aux bons de livraison
export const READONLY_BL_ROLES = ['production', 'be', 'gestion'];

// Rôles cantonnés aux seules factures (lecture + actions du workflow facture)
export const FACTURE_ONLY_ROLES = ['comptabilite'];

export function isReadOnlyBLRole(role) {
    return READONLY_BL_ROLES.includes(role);
}

export function isFactureOnlyRole(role) {
    return FACTURE_ONLY_ROLES.includes(role);
}

/**
 * Code du type de document auquel un rôle est cantonné.
 * Retourne null si le rôle n'est soumis à aucune restriction de type.
 */
export function getRestrictedTypeCode(role) {
    if (isReadOnlyBLRole(role)) return BL_TYPE_CODE;
    if (isFactureOnlyRole(role)) return FACTURE_TYPE_CODE;
    return null;
}

// Types de documents concernés par les restrictions workflow BL/Facture
// bon-de-fourniture-client utilise le workflow générique (pas dans cette liste)
export const WORKFLOW_DOC_TYPES = ['bon-de-livraison', 'facture'];

// ============================================================
// Statuts workflow par type de document
// ============================================================

// Statuts BL (Bon de Livraison)
export const BL_STATUSES = {
    en_attente: { label: 'En attente', color: 'pending', icon: 'clock' },
    en_cours_gpao: { label: 'Import BL réception', color: 'info', icon: 'gear' },
    bl_receptionne: { label: 'BL réceptionné', color: 'info', icon: 'box' },
    entree_gpao_validee: { label: 'BL saisie dans CLIPPER', color: 'progress', icon: 'gear' },
    en_attente_facture: { label: 'Facture', color: 'success', icon: 'file-earmark-check' },
    materiel_fourniture_client: { label: 'Fourniture client', color: 'fourniture', icon: 'truck' },
    refuse: { label: 'Refusé', color: 'danger', icon: 'x-circle' },
    archive: { label: 'Archivé', color: 'archive', icon: 'archive' },
};

// Statuts Facture
export const FACTURE_STATUSES = {
    en_attente: { label: 'En attente', color: 'pending', icon: 'clock' },
    facture_recue: { label: 'Facture reçue', color: 'info', icon: 'envelope' },
    facture_bloquee_gpao: { label: 'Bloquée (GPAO)', color: 'warning', icon: 'exclamation-triangle' },
    facture_conforme: { label: 'Conforme achats', color: 'progress', icon: 'clipboard-check' },
    facture_validee_compta: { label: 'Validée comptabilité', color: 'success', icon: 'calculator' },
    facture_payee: { label: 'Payée', color: 'paid', icon: 'cash-coin' },
    refuse: { label: 'Refusée', color: 'danger', icon: 'x-circle' },
    archive: { label: 'Archivée', color: 'archive', icon: 'archive' },
};

// Tous les statuts possibles (union)
export const ALL_STATUSES = {
    en_attente: { label: 'En attente', color: 'pending' },
    valide: { label: 'Validé', color: 'success' },
    en_cours_gpao: { label: 'Import BL réception', color: 'info' },
    bl_receptionne: { label: 'BL réceptionné', color: 'info' },
    entree_gpao_validee: { label: 'BL saisie dans CLIPPER', color: 'progress' },
    bl_valide: { label: 'BL validé', color: 'success' },
    en_attente_facture: { label: 'Facture', color: 'success' },
    materiel_fourniture_client: { label: 'Fourniture client', color: 'fourniture' },
    facture_recue: { label: 'Facture reçue', color: 'info' },
    facture_bloquee_gpao: { label: 'Bloquée (GPAO)', color: 'warning' },
    facture_conforme: { label: 'Conforme achats', color: 'progress' },
    facture_validee_compta: { label: 'Validée comptabilité', color: 'success' },
    facture_payee: { label: 'Payée', color: 'paid' },
    refuse: { label: 'Refusé', color: 'danger' },
    archive: { label: 'Archivé', color: 'archive' },
};

// ============================================================
// Transitions autorisées par type de document
// Clé = statut source → Valeur = liste de statuts cibles autorisés
// ============================================================

// BL Simef (magasin_id = 1) : après validation GPAO, attente de la facture fournisseur
export const BL_TRANSITIONS = {
    en_attente: ['en_cours_gpao', 'refuse'],
    en_cours_gpao: ['entree_gpao_validee', 'en_attente'],
    entree_gpao_validee: ['en_attente_facture', 'en_attente'],
    en_attente_facture: ['archive', 'en_attente'],
    refuse: ['en_attente'],
    archive: ['en_attente'],
};

// BL hors Simef (magasin_id != 1) : réception → GPAO → fourniture client → fin
export const BL_EXTERNE_TRANSITIONS = {
    en_attente: ['bl_receptionne', 'refuse'],
    bl_receptionne: ['entree_gpao_validee', 'refuse', 'en_attente'],
    entree_gpao_validee: ['materiel_fourniture_client', 'en_attente'],
    materiel_fourniture_client: ['archive', 'en_attente'],
    refuse: ['en_attente'],
    archive: ['en_attente'],
};

export const FACTURE_TRANSITIONS = {
    en_attente: ['facture_recue', 'refuse'],
    facture_recue: ['facture_conforme', 'facture_bloquee_gpao', 'refuse', 'en_attente'],
    facture_bloquee_gpao: ['facture_recue', 'en_attente'],
    facture_conforme: ['facture_validee_compta', 'facture_bloquee_gpao', 'en_attente'],
    facture_validee_compta: ['facture_payee', 'en_attente'],
    facture_payee: ['archive', 'en_attente'],
    refuse: ['en_attente'],
    archive: ['en_attente'],
};

// Transitions génériques (pour documents hors BL/Facture)
// Workflow simplifié : En attente / Validé / Refusé uniquement
export const GENERIC_TRANSITIONS = {
    en_attente: ['valide', 'refuse'],
    valide: ['en_attente'],
    refuse: ['en_attente'],
};

// ============================================================
// Permissions par transition (quel rôle peut déclencher quel statut cible)
// ============================================================

// NB : doit rester aligné sur $transition_permissions dans api/scan-document/updateWorkflowStatus.php
export const TRANSITION_PERMISSIONS = {
    // --- BL ---
    en_cours_gpao: ['admin'],
    bl_receptionne: ['admin'],
    entree_gpao_validee: ['admin'],
    bl_valide: ['admin'],
    en_attente_facture: ['admin'],
    materiel_fourniture_client: ['admin'],

    // --- Facture ---
    facture_recue: ['admin', 'comptabilite'],
    facture_bloquee_gpao: ['admin', 'comptabilite'],
    facture_conforme: ['admin'],
    facture_validee_compta: ['admin', 'comptabilite'],
    facture_payee: ['admin', 'comptabilite'],

    // --- Communs ---
    en_attente: ['admin', 'comptabilite'],
    refuse: ['admin', 'comptabilite'],
    archive: ['admin'],

    // --- Générique (non BL/Facture) ---
    valide: ['admin'],
};

// ============================================================
// Permissions par fonctionnalité
// ============================================================

export const SCAN_DOC_PERMISSIONS = {
    // Accès à la page scan-documents
    // (production/be/gestion : lecture des BL — comptabilite : factures uniquement)
    access: ['admin', ...READONLY_BL_ROLES, ...FACTURE_ONLY_ROLES],

    // Upload / scanner des documents
    upload: ['admin'],

    // Modifier les métadonnées d'un document (bouton "Modifier" + formulaire édition)
    edit_metadata: ['admin'],

    // Supprimer un document
    delete_document: ['admin'],

    // --- Liaison documents ---
    link_documents: ['admin'],

    // --- Entreprises ---
    manage_companies: ['admin'],

    // --- Données financières ---
    view_financial: ['admin', ...FACTURE_ONLY_ROLES],

    // --- Export ---
    export_csv: ['admin', ...FACTURE_ONLY_ROLES],

    // --- Déverrouiller un document archivé ---
    unlock_archive: ['admin'],
};

// ============================================================
// Fonctions utilitaires
// ============================================================

/**
 * Vérifie si un rôle a une permission fonctionnelle
 */
export function hasPermission(role, permission) {
    const allowedRoles = SCAN_DOC_PERMISSIONS[permission];
    if (!allowedRoles) return false;
    return allowedRoles.includes(role);
}

/**
 * Retourne toutes les permissions d'un rôle sous forme d'objet { permission: boolean }
 */
export function getPermissionsForRole(role) {
    const perms = {};
    for (const [key, roles] of Object.entries(SCAN_DOC_PERMISSIONS)) {
        perms[key] = roles.includes(role);
    }
    return perms;
}

/**
 * Vérifie si un type de document est soumis aux restrictions workflow BL/Facture
 */
export function isWorkflowRestricted(typeCode) {
    return WORKFLOW_DOC_TYPES.includes(typeCode);
}

/**
 * Retourne le libellé formaté d'un statut workflow
 */
export function getStatusLabel(status) {
    return ALL_STATUSES[status]?.label || status || 'En attente';
}

/**
 * Retourne la couleur CSS d'un statut
 */
export function getStatusColor(status) {
    return ALL_STATUSES[status]?.color || 'pending';
}

/**
 * Retourne les transitions possibles depuis un statut donné, 
 * filtrées par type de document et rôle utilisateur
 * @param {string} currentStatus - Statut actuel du document
 * @param {string} documentTypeCode - Code du type de document ('facture', 'bon-de-livraison', etc.)
 * @param {string} userRole - Rôle de l'utilisateur
 * @param {object} options - Options supplémentaires { magasinId }
 * @returns {Array<{status: string, label: string, color: string}>}
 */
export function getAvailableTransitions(currentStatus, documentTypeCode, userRole, options = {}) {
    const current = currentStatus || 'en_attente';
    const magasinId = options.magasinId ? parseInt(options.magasinId) : null;

    // Déterminer la table de transitions selon le type
    let transitions;
    if (documentTypeCode === 'bon-de-livraison') {
        // Magasin Simef (id=1) : workflow complet, sinon : arrêt après GPAO
        transitions = (magasinId === 1) ? BL_TRANSITIONS : BL_EXTERNE_TRANSITIONS;
    } else if (documentTypeCode === 'facture') {
        transitions = FACTURE_TRANSITIONS;
    } else {
        // bon-de-fourniture-client et tous les autres : workflow générique
        transitions = GENERIC_TRANSITIONS;
    }

    // Statuts cibles possibles depuis le statut actuel
    const possibleTargets = transitions[current] || [];

    // Cas spécial : déverrouillage archive (admin uniquement)
    if (current === 'archive') {
        if (SCAN_DOC_PERMISSIONS.unlock_archive.includes(userRole)) {
            return [{ status: 'en_attente', label: 'Déverrouiller (remettre en attente)', color: 'pending' }];
        }
        return [];
    }

    // Filtrer par permissions du rôle
    return possibleTargets
        .filter(targetStatus => {
            const allowedRoles = TRANSITION_PERMISSIONS[targetStatus];
            return allowedRoles && allowedRoles.includes(userRole);
        })
        .map(targetStatus => ({
            status: targetStatus,
            label: ALL_STATUSES[targetStatus]?.label || targetStatus,
            color: ALL_STATUSES[targetStatus]?.color || 'pending',
        }));
}
